import { scaleLinear } from 'd3-scale';

/**
 * A escala temporal da timeline.
 *
 * Anos astronomicos assinados, nunca `Date`: `scaleTime` falha antes de 1 dC
 * em varias implementacoes e nao sabe representar intervalos. Aqui o dominio e
 * so numeros, e a aritmetica de eras vive em `@et/schema/date`.
 */

export type AxisMode = 'split' | 'continuous';

export interface Region {
  readonly domain: readonly [number, number];
  readonly range: readonly [number, number];
}

export interface Tick {
  readonly year: number;
  readonly x: number;
  /** Primeiro tick de cada regiao, para a UI poder marcar a quebra. */
  readonly regionIndex: number;
}

export interface TimeScale {
  readonly mode: AxisMode;
  readonly regions: readonly Region[];
  /** Intervalos de x sem dominio: onde o eixo esta partido. */
  readonly gaps: readonly (readonly [number, number])[];
  /** `null` quando o ano cai na quebra — nao ha onde o desenhar. */
  toX(year: number): number | null;
  fromX(x: number): number | null;
  ticks(target?: number): Tick[];
}

const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi);

function build(regions: readonly Region[], mode: AxisMode): TimeScale {
  const scales = regions.map((r) =>
    scaleLinear().domain([...r.domain]).range([...r.range]));

  const gaps: (readonly [number, number])[] = [];
  for (let i = 1; i < regions.length; i += 1) {
    const prev = regions[i - 1]!;
    const next = regions[i]!;
    if (next.range[0] > prev.range[1]) gaps.push([prev.range[1], next.range[0]] as const);
  }

  return {
    mode,
    regions,
    gaps,

    toX(year) {
      for (let i = 0; i < regions.length; i += 1) {
        const r = regions[i]!;
        if (year >= r.domain[0] && year <= r.domain[1]) return scales[i]!(year);
      }
      // Antes da primeira regiao ou depois da ultima: fixa na borda, para um
      // intervalo que entra parcialmente no ecra continuar a ser desenhavel.
      const first = regions[0]!;
      const last = regions[regions.length - 1]!;
      if (year < first.domain[0]) return first.range[0];
      if (year > last.domain[1]) return last.range[1];
      return null; // cai numa quebra
    },

    fromX(x) {
      for (let i = 0; i < regions.length; i += 1) {
        const r = regions[i]!;
        if (x >= r.range[0] && x <= r.range[1]) return scales[i]!.invert(x);
      }
      return null;
    },

    ticks(target = 8) {
      const totalWidth = regions.reduce((n, r) => n + (r.range[1] - r.range[0]), 0);
      const out: Tick[] = [];
      regions.forEach((r, i) => {
        const share = (r.range[1] - r.range[0]) / totalWidth;
        const count = Math.max(2, Math.round(target * share));
        for (const year of scales[i]!.ticks(count)) {
          if (year < r.domain[0] || year > r.domain[1]) continue;
          out.push({ year: Math.round(year), x: scales[i]!(year), regionIndex: i });
        }
      });
      return out;
    },
  };
}

export interface ContinuousOptions {
  readonly domain: readonly [number, number];
  readonly range: readonly [number, number];
}

export const continuousScale = ({ domain, range }: ContinuousOptions): TimeScale =>
  build([{ domain, range }], 'continuous');

export interface SplitOptions {
  /** Antiguidade: onde a investigacao acontece. */
  readonly antiquity: readonly [number, number];
  /** Descobertas modernas: onde os objectos foram encontrados. */
  readonly discovery: readonly [number, number];
  readonly range: readonly [number, number];
  /** Fraccao da largura dada a faixa de descoberta. */
  readonly discoveryShare?: number;
  readonly gapWidth?: number;
}

/**
 * §6 — a Antiguidade e a era das descobertas no mesmo ecra, com uma quebra
 * explicita entre elas. A curva que liga o objecto a sua descoberta atravessa
 * a quebra: e a assinatura visual do produto.
 */
export function splitScale({
  antiquity, discovery, range, discoveryShare = 0.18, gapWidth = 28,
}: SplitOptions): TimeScale {
  const [x0, x1] = range;
  const usable = x1 - x0 - gapWidth;
  const share = clamp(discoveryShare, 0.05, 0.5);
  const discoveryWidth = usable * share;
  const antiquityWidth = usable - discoveryWidth;

  return build([
    { domain: antiquity, range: [x0, x0 + antiquityWidth] },
    { domain: discovery, range: [x0 + antiquityWidth + gapWidth, x1] },
  ], 'split');
}

// ── viewport: pan e zoom ─────────────────────────────────────

export interface Viewport {
  readonly domain: readonly [number, number];
}

export interface ViewportLimits {
  readonly full: readonly [number, number];
  /** Menor janela permitida, em anos. Evita zoom infinito. */
  readonly minSpan?: number;
}

const spanOf = (v: Viewport) => v.domain[1] - v.domain[0];

/** Zoom em torno de um ponto focal, para o ano sob o cursor nao se mover. */
export function zoomAt(
  viewport: Viewport, limits: ViewportLimits, focusYear: number, factor: number,
): Viewport {
  const full = limits.full;
  const minSpan = limits.minSpan ?? 5;
  const maxSpan = full[1] - full[0];
  const span = clamp(spanOf(viewport) * factor, minSpan, maxSpan);

  const t = (focusYear - viewport.domain[0]) / spanOf(viewport);
  let lo = focusYear - t * span;
  let hi = lo + span;

  if (lo < full[0]) { lo = full[0]; hi = lo + span; }
  if (hi > full[1]) { hi = full[1]; lo = hi - span; }
  return { domain: [Math.max(lo, full[0]), Math.min(hi, full[1])] };
}

/** Pan por um deslocamento em anos, sem sair dos limites. */
/**
 * Centra a janela num ano sem lhe mudar o tamanho. Os limites continuam a
 * valer: pedir 1300 aC com o eixo a comecar em 800 aC encosta a janela ao
 * inicio, que e o mais perto que se consegue chegar.
 */
export function centerViewport(viewport: Viewport, limits: ViewportLimits, year: number): Viewport {
  const span = spanOf(viewport);
  return panBy(viewport, limits, (year - span / 2) - viewport.domain[0]);
}

export function panBy(viewport: Viewport, limits: ViewportLimits, deltaYears: number): Viewport {
  const span = spanOf(viewport);
  const full = limits.full;
  let lo = viewport.domain[0] + deltaYears;
  if (lo < full[0]) lo = full[0];
  if (lo + span > full[1]) lo = full[1] - span;
  return { domain: [lo, lo + span] };
}

export const fullViewport = (limits: ViewportLimits): Viewport => ({ domain: limits.full });
